import { Divider, Typography, Empty, Spin } from "antd";
import { clsx } from "clsx";

import { ProfileLookup } from "Components/shared/ProfileLookup/ProfileLookup";
import { JourneyExecution } from "Services/Journeys.service";
import { Profile } from "Types/Profile";

import { formatDate, getStatusKey, getStatusLabel } from "./helpers";

import styles from "./ListView.module.scss";

const { Text } = Typography;

const SEARCH_PLACEHOLDER = "Search by Name, Phone, Email or External ID";

type ListViewProps = {
    journeyId: string | null | undefined;
    profileId: string;
    selectedProfileName: string;
    searchKey: number;
    loading: boolean;
    executions: JourneyExecution[];
    handleProfileSelect: (selectedProfileId: string, profile: Partial<Profile>) => Promise<void>;
    handleExecutionClick: (execution: JourneyExecution) => Promise<void>;
    handleClearAll: () => void;
};

export function ListView({
    journeyId,
    profileId,
    selectedProfileName,
    searchKey,
    loading,
    executions,
    handleProfileSelect,
    handleExecutionClick,
    handleClearAll,
}: ListViewProps): JSX.Element {
    return (
        <>
            <div className={styles.stickySearch}>
                <div className={styles.searchSection}>
                    <ProfileLookup
                        key={searchKey}
                        onProfileSelect={handleProfileSelect}
                        disabled={!journeyId}
                        placeholder={SEARCH_PLACEHOLDER}
                        className={styles.profileLookup}
                    />
                    {!journeyId && (
                        <Text type="secondary" className={styles.hint}>
                            Save the journey first to enable monitoring
                        </Text>
                    )}
                </div>
                <Divider className={styles.divider} />
            </div>

            {loading ? (
                <div className={styles.loadingState}>
                    <Spin size="large" />
                    <Text type="secondary" className={styles.loadingText}>
                        Loading executions...
                    </Text>
                </div>
            ) : executions.length > 0 ? (
                <>
                    <div className={styles.executionsHeader}>
                        <div className={styles.listTitleWrapper}>
                            {selectedProfileName ? (
                                <span className={styles.listTitleRow}>
                                    <Text
                                        strong
                                        className={styles.listTitle}
                                        ellipsis={{ tooltip: `Executions for ${selectedProfileName}` }}
                                    >
                                        Executions for {selectedProfileName}
                                    </Text>
                                    <Text strong className={styles.listTitleCount}>
                                        ({executions.length})
                                    </Text>
                                </span>
                            ) : (
                                <Text strong className={styles.listTitle}>
                                    Executions ({executions.length})
                                </Text>
                            )}
                        </div>
                        <a className={styles.clearAllLink} onClick={handleClearAll}>
                            Clear All
                        </a>
                    </div>
                    <div className={styles.executionsList}>
                        {executions.map((execution) => (
                            <div key={execution.executionName} className={styles.executionCard}>
                                <div
                                    className={styles.executionContent}
                                    onClick={() => handleExecutionClick(execution)}
                                >
                                    <div className={styles.executionInfo}>
                                        <Text
                                            strong
                                            className={styles.executionName}
                                            ellipsis={{ tooltip: selectedProfileName || execution.executionName }}
                                        >
                                            {selectedProfileName || execution.executionName}
                                        </Text>
                                        <Text type="secondary" className={styles.executionDate}>
                                            Started: {formatDate(execution.startedAt)}
                                        </Text>
                                    </div>
                                    <span
                                        className={clsx(
                                            styles.statusTag,
                                            styles[`statusTag_${getStatusKey(execution.status)}`]
                                        )}
                                    >
                                        {getStatusLabel(execution.status)}
                                    </span>
                                </div>
                                <Divider className={styles.cardDivider} />
                            </div>
                        ))}
                    </div>
                </>
            ) : (
                profileId && (
                    <div className={styles.emptyState}>
                        <Empty description="No executions found" image={Empty.PRESENTED_IMAGE_SIMPLE} />
                    </div>
                )
            )}
        </>
    );
}
